import React, { useEffect, useState } from "react";
import { api } from "../../services/api";
import {
    Phone,
    MapPin,
    Clock,
    Loader2,
    Search,
    Store,
    Syringe,
    Scissors,
    Dog,
    Sparkles,
    Navigation,
    PhoneCall,
    Building2,
    Star,
    Shield,
    CalendarDays,
    AlertCircle,
} from "lucide-react";

// Categorías según el ENUM de tu tabla
const CATEGORIAS = [
    { value: "veterinaria", label: "Veterinaria", icon: Syringe },
    { value: "paseador", label: "Paseador", icon: Dog },
    { value: "petshop", label: "Pet Shop", icon: Store },
    { value: "guarderia", label: "Guardería", icon: Building2 },
    { value: "otro", label: "Otros", icon: Building2 },
];

const CATEGORIA_COLORS = {
    veterinaria: { bg: "bg-blue-50", text: "text-blue-700", badge: "bg-blue-100 text-blue-800", border: "border-blue-400" },
    paseador: { bg: "bg-emerald-50", text: "text-emerald-700", badge: "bg-emerald-100 text-emerald-800", border: "border-emerald-400" },
    petshop: { bg: "bg-purple-50", text: "text-purple-700", badge: "bg-purple-100 text-purple-800", border: "border-purple-400" },
    guarderia: { bg: "bg-amber-50", text: "text-amber-700", badge: "bg-amber-100 text-amber-800", border: "border-amber-400" },
    otro: { bg: "bg-gray-50", text: "text-gray-700", badge: "bg-gray-100 text-gray-800", border: "border-gray-400" },
};

export default function Socios() {
    const [socios, setSocios] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filtro, setFiltro] = useState("");
    const [busqueda, setBusqueda] = useState("");

    useEffect(() => {
        cargarSocios();
    }, []);

    const cargarSocios = async () => {
        try {
            const res = await api.getSocios();
            setSocios(res.data || []);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const sociosFiltrados = socios.filter((s) => {
        const tipo = (s.tipo_servicio || "").toLowerCase();
        const nombre = (s.nombre_local || s.nombre || "").toLowerCase();
        const matchCategoria = !filtro || tipo === filtro.toLowerCase();
        const matchTexto =
            !busqueda ||
            nombre.includes(busqueda.toLowerCase()) ||
            (s.nombre || "").toLowerCase().includes(busqueda.toLowerCase()) ||
            (s.apellido || "").toLowerCase().includes(busqueda.toLowerCase()) ||
            (s.whatsapp || "").includes(busqueda);
        return matchCategoria && matchTexto;
    });

    const limpiarTelefono = (telefono) => {
        if (!telefono) return "";
        return String(telefono).replace(/[^\d]/g, "");
    };

    const getWhatsappLink = (telefono) => {
        const tel = limpiarTelefono(telefono);
        if (!tel) return "#";
        const telefonoFinal = tel.startsWith("54") ? tel : `549${tel}`;
        return `https://wa.me/${telefonoFinal}`;
    };

    const getPhotoUrl = (logoUrl) => {
        if (!logoUrl) return null;
        if (logoUrl.startsWith("http")) return logoUrl;
        const base = process.env.REACT_APP_API_URL || "http://localhost/api-smartpet";
        return `${base.replace(/\/$/, "")}/${logoUrl.replace(/^\//, "")}`;
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-sp-light flex items-center justify-center">
                <Loader2 className="w-10 h-10 text-sp-primary animate-spin" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-sp-light mt-16">
            {/* ========== HEADER ========== */}
            <section className="bg-gradient-to-br from-sp-dark to-sp-primary pt-6 pb-12 px-4 xs:px-5 relative overflow-hidden">
                <div className="max-w-[1100px] mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-white text-xl xs:text-2xl font-black flex items-center gap-2">
                            <Store className="w-7 h-7" /> Socios Comerciales
                        </h1>
                        <p className="text-white/70 text-sm font-semibold mt-1">
                            {socios.length === 0
                                ? 'Aún no hay socios registrados'
                                : `${socios.length} socio${socios.length > 1 ? 's' : ''} para cuidar a tu mascota`}
                        </p>
                    </div>
                    {socios.length > 0 && (
                        <span className="bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-bold">
                            {sociosFiltrados.length} disponibles
                        </span>
                    )}
                </div>
                <div
                    className="absolute -bottom-0.5 left-0 right-0 h-8 bg-sp-light"
                    style={{ clipPath: 'ellipse(55% 100% at 50% 100%)' }}
                />
            </section>

            {/* ========== FILTROS Y BÚSQUEDA ========== */}
            <div className="max-w-[1100px] mx-auto px-4 xs:px-5 pt-4 pb-6">
                {/* Filtros */}
                <div className="flex flex-wrap gap-2 mb-4">
                    <button
                        onClick={() => setFiltro("")}
                        className={`px-4 py-1.5 rounded-full text-sm font-bold transition ${filtro === ""
                            ? "bg-sp-primary text-white shadow-md"
                            : "bg-white text-gray-600 hover:bg-gray-50 shadow-sm"
                            }`}
                    >
                        Todos
                    </button>
                    {CATEGORIAS.map((cat) => {
                        const Icon = cat.icon;
                        const isActive = filtro === cat.value;
                        return (
                            <button
                                key={cat.value}
                                onClick={() => setFiltro(isActive ? "" : cat.value)}
                                className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-bold transition ${isActive
                                    ? "bg-sp-primary text-white shadow-md"
                                    : "bg-white text-gray-600 hover:bg-gray-50 shadow-sm"
                                    }`}
                            >
                                <Icon size={14} />
                                {cat.label}
                            </button>
                        );
                    })}
                </div>

                {/* Buscador */}
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-sp-muted" />
                    <input
                        type="text"
                        placeholder="Buscar por nombre, local o teléfono..."
                        value={busqueda}
                        onChange={(e) => setBusqueda(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 bg-white rounded-xl shadow-sp text-sm font-semibold placeholder:text-sp-muted/60 focus:outline-none focus:ring-2 focus:ring-sp-primary/30"
                    />
                </div>
            </div>

            {/* ========== LISTA DE SOCIOS ========== */}
            <div className="max-w-[1100px] mx-auto px-4 xs:px-5 pb-10">
                {sociosFiltrados.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 text-center bg-white rounded-3xl shadow-sp">
                        {socios.length === 0 ? (
                            <>
                                <Store className="w-16 h-16 text-sp-primary/30 mb-4" />
                                <h3 className="text-sp-text font-black text-lg mb-2">No hay socios registrados</h3>
                                <p className="text-sp-muted font-semibold">Vuelve más tarde para encontrar servicios para tu mascota</p>
                            </>
                        ) : (
                            <>
                                <Search className="w-16 h-16 text-sp-primary/30 mb-4" />
                                <h3 className="text-sp-text font-black text-lg mb-2">No se encontraron resultados</h3>
                                <p className="text-sp-muted font-semibold">Probá con otros filtros o términos de búsqueda</p>
                                <button
                                    onClick={() => { setFiltro(""); setBusqueda(""); }}
                                    className="mt-4 text-sp-primary font-bold text-sm hover:underline"
                                >
                                    Limpiar filtros
                                </button>
                            </>
                        )}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 xs:gap-6">
                        {sociosFiltrados.map((socio) => {
                            const categoria = socio.tipo_servicio || "otro";
                            const colors = CATEGORIA_COLORS[categoria] || CATEGORIA_COLORS.otro;
                            const Icon = CATEGORIAS.find(c => c.value === categoria)?.icon || Building2;

                            const nombreCompleto = socio.nombre_local || `${socio.nombre} ${socio.apellido || ''}`.trim();
                            const logo = getPhotoUrl(socio.logo_url);

                            const tieneWhatsapp = socio.whatsapp && socio.whatsapp.trim();

                            return (
                                <div
                                    key={socio.id}
                                    className="group bg-white rounded-2xl shadow-sp hover:shadow-sp-hover transition-all overflow-hidden border border-transparent hover:border-sp-primary/20"
                                >
                                    {/* Header con logo y categoría */}
                                    <div className={`px-4 pt-4 pb-3 flex items-start gap-3 border-b ${colors.border} border-opacity-30`}>
                                        <div className={`w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 ${colors.bg} ${colors.text}`}>
                                            {logo ? (
                                                <img
                                                    src={logo}
                                                    alt={nombreCompleto}
                                                    className="w-full h-full object-cover rounded-2xl"
                                                />
                                            ) : (
                                                <Icon size={28} />
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h3 className="font-black text-sp-text text-base truncate">
                                                {nombreCompleto || "Sin nombre"}
                                            </h3>
                                            <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
                                                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${colors.badge}`}>
                                                    {categoria.charAt(0).toUpperCase() + categoria.slice(1)}
                                                </span>
                                                {socio.servicio_24h == 1 && (
                                                    <span className="flex items-center gap-0.5 text-xs font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">
                                                        <Clock size={10} /> 24h
                                                    </span>
                                                )}
                                                {socio.emergencias == 1 && (
                                                    <span className="flex items-center gap-0.5 text-xs font-bold text-red-600 bg-red-50 px-2 py-0.5 rounded-full">
                                                        <AlertCircle size={10} /> Emergencias
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Cuerpo */}
                                    <div className="p-4 space-y-2">
                                        {socio.dias_atencion && (
                                            <div className="flex items-start gap-2 text-sm text-sp-body">
                                                <CalendarDays size={16} className="text-sp-muted flex-shrink-0 mt-0.5" />
                                                <span className="line-clamp-2">{socio.dias_atencion}</span>
                                            </div>
                                        )}
                                        {socio.horarios_atencion && (
                                            <div className="flex items-start gap-2 text-sm text-sp-body">
                                                <Clock size={16} className="text-sp-muted flex-shrink-0 mt-0.5" />
                                                <span className="line-clamp-2">{socio.horarios_atencion}</span>
                                            </div>
                                        )}
                                        {socio.nombre && socio.apellido && (
                                            <p className="text-xs text-sp-muted mt-1">
                                                Contacto: {socio.nombre} {socio.apellido}
                                            </p>
                                        )}
                                    </div>

                                    {/* Botones de acción */}
                                    <div className="px-4 pb-4 flex flex-wrap gap-2">
                                        {tieneWhatsapp && (
                                            <a
                                                href={getWhatsappLink(socio.whatsapp)}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="flex-1 min-w-[100px] flex items-center justify-center gap-1.5 px-3 py-2 bg-green-50 text-green-700 rounded-xl font-bold text-sm hover:bg-green-100 transition"
                                            >
                                                <Phone size={14} /> WhatsApp
                                            </a>
                                        )}
                                        {tieneWhatsapp && (
                                            <a
                                                href={`tel:${limpiarTelefono(socio.whatsapp)}`}
                                                className="flex-1 min-w-[100px] flex items-center justify-center gap-1.5 px-3 py-2 bg-blue-50 text-blue-700 rounded-xl font-bold text-sm hover:bg-blue-100 transition"
                                            >
                                                <PhoneCall size={14} /> Llamar
                                            </a>
                                        )}
                                        {!tieneWhatsapp && (
                                            <span className="text-xs text-sp-muted w-full text-center">Sin información de contacto</span>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}